import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import {useState} from 'react';
import type {Event} from '../entities/Event';
import {getCookie, setCookie} from '../util/cookieUtils';
import {remainingDays} from '../util/eventUtils';
import Snowfall from './Snowfall';

const COOKIE = 'last_notification_remaining_days';

interface Props {
  /** The current event; the dialog stays hidden while it is not loaded. */
  event?: Event;
  onDialogClosed(): void;
}

/** "Nur noch X Tage!" - shown once per day until the event is over. */
export default function RemainingDaysDialog({event, onDialogClosed}: Props) {
  const [lastNotificationRemainingDays, setLastNotificationRemainingDays] = useState(
    () => parseInt(getCookie(COOKIE) || '-1', 10),
  );

  if (!event) {
    return null;
  }
  const days = remainingDays(event);
  if (days < 0) {
    return null;
  }
  const close = () => {
    setCookie(COOKIE, days.toString());
    setLastNotificationRemainingDays(days);
    onDialogClosed();
  };
  return (
    <Dialog open={lastNotificationRemainingDays !== days} keepMounted={true} onClose={close}>
      <DialogContent sx={{padding: 0}}>
        <Box
          sx={theme => ({
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center',
            padding: {xs: '32px 24px 28px', sm: '40px 48px 36px'},
            color: theme.palette.secondary.contrastText,
            backgroundImage: `linear-gradient(170deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
          })}
        >
          <Snowfall/>
          <Box sx={{position: 'relative'}}>
            <Typography variant="overline" sx={{fontSize: '0.8rem', opacity: 0.85, lineHeight: 1.5}}>
              Adventskalender
            </Typography>
            <Typography variant="h5" sx={{marginTop: 0.5}}>
              Nur noch
            </Typography>
            <Box
              sx={theme => ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                marginY: 2.5,
                minWidth: {xs: 140, sm: 180},
                paddingX: 3,
                paddingY: 1,
                borderRadius: '20px',
                color: theme.palette.secondary.dark,
                backgroundColor: '#ffffff',
                boxShadow: `0 24px 40px -20px ${alpha('#000000', 0.5)}`,
                fontSize: {xs: '5.5rem', sm: '7rem'},
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.04em',
                // the upper half is slightly shaded, like a little calendar door
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '50%',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                  borderBottom: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                },
              })}
            >
              {days}
            </Box>
            <Typography variant="h5">
              {days === 1 ? 'Tag!' : 'Tage!'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{padding: 3, textAlign: 'center'}}>
          <Button variant="contained" color="secondary" size="large" onClick={close} sx={{minWidth: 180}}>
            Los geht&apos;s
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
